/**
 * MapCard.tsx — shared "Map" panel (live Google map, full screen) used by
 * any template that has a `Locations`-style `ValueRow[]` of addresses.
 *
 * Used to offer a schematic (bundled, no-API-key) world map with a toggle
 * to switch to the live Google map. Dropped the schematic map everywhere —
 * across every result template — leaving just the live map, always on, no
 * toggle. `WorldMap`/`useHighlightedCountries` (and the pan/zoom plumbing
 * only they needed) are gone; `LiveMap` below is now the entire card body.
 *
 * Split out of PersonTemplate.tsx (where this was originally built for
 * phone/email/deepweb results) so NameScanTemplate (combined_name) could
 * reuse the exact same panel for PLUTO/webmii/allServicesNames location data
 * instead of re-implementing country matching, the Google Maps loader, and
 * the full-screen dialog a second time. Nothing about the behaviour changed
 * in the move — see the inline comments below, carried over verbatim.
 */
import * as React from 'react'
import { Box, Dialog, Typography } from '@mui/material'
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api'
import { brand } from '../vendor/searchTokens'
import { ResCard, useTokens, ValueRow } from './primitives'
import { radii } from '../tokens'

/*
 * Admin copy: the dashboard version also places "country only" pins by
 * matching address strings against world-atlas country shapes (d3-geo,
 * topojson-client, world-atlas). Those libraries aren't in the admin, so this
 * copy plots exact coordinates only, on the plain Google map. Addresses that
 * have no coordinates are simply not pinned.
 */

/** Small +/− control cluster in the live map's corner — a local copy of the
 * app's usual NavBtn look, kept private to this file so this module has no
 * dependency back on any template file. */
function MapNavBtn({ disabled, onClick, children }: { disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  const tk = useTokens()
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 19, height: 19, borderRadius: '6px', border: `1px solid ${tk.border}`, background: tk.surface2,
        color: tk.text2, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
        display: 'grid', placeItems: 'center', fontSize: 10.5, fontFamily: 'inherit', flex: 'none',
        '&:hover': disabled ? {} : { borderColor: tk.borderStrong, color: tk.text, background: tk.surface3 },
      }}
    >
      {children}
    </Box>
  )
}

type LivePin = { key: string; lat: number; lng: number; label: string; precision: 'exact' | 'country' }

/**
 * Same two signals `LiveMap` draws (exact `mapPoints` coordinates, and
 * `addresses` matched against a country name/ISO code) reduced to the plain
 * pin list, with no React/hooks involved — factored out of `LiveMap` so
 * `hasMapPins` below (and any caller deciding whether to render a `MapCard`
 * at all) can reuse the exact same matching logic instead of re-guessing it
 * from `addresses.length`, which says nothing about whether any of those
 * strings will actually resolve to a point on the map.
 */
// `addresses` stays in the signature so callers don't change; see the note above.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function computeMapPins(mapPoints: any[], addresses: ValueRow[]): LivePin[] {
  const exactPins: LivePin[] = (mapPoints || [])
    .map((p: any, i: number): LivePin | null => {
      const lat = typeof p?.lat === 'string' ? parseFloat(p.lat) : p?.lat
      const lng = typeof p?.lng === 'string' ? parseFloat(p.lng) : p?.lng
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
      return { key: `p-${p.id ?? i}`, lat, lng, label: p.title || 'Location', precision: 'exact' }
    })
    .filter((p): p is LivePin => p != null)

  return exactPins
}

/**
 * Whether `MapCard` would actually plot anything for this data — i.e.
 * whether it would show real pins rather than its own "No geolocation
 * provided" empty state. A non-empty `addresses` list is not enough on its
 * own: a value like "Los Angeles, CA" has no country name/ISO code
 * `computeMapPins` can match, so the card would render pinless anyway.
 * Callers deciding whether to render a `MapCard` at all (or reserve layout
 * space for one, e.g. RedditTemplate/PersonTemplate's 2×2 grid slot) should
 * gate on this instead of just `addresses.length > 0`.
 */
export function hasMapPins(mapPoints: any[], addresses: ValueRow[]): boolean {
  return computeMapPins(mapPoints || [], addresses || []).length > 0
}

/**
 * Live Google-Maps view — now the Map card's ONLY body (see the file header
 * comment; the schematic world map + toggle this used to sit behind are
 * gone). Draws both signals it can be handed — exact points vs matched
 * country names/ISO codes — together on one real map: an exact `mapPoints`
 * hit gets a solid pin, an address that only matched a country gets a
 * dashed circle at that country's centroid — the same "country only" visual
 * language irbispro-v2's own MapView uses for dialling-prefix results, so
 * the distinction reads the same way anywhere in the app. A country whose
 * circle would land on top of an exact pin is skipped; the precise pin
 * already covers it.
 */
function LiveMap({ mapPoints, addresses }: { mapPoints: any[]; addresses: ValueRow[] }) {
  const tk = useTokens()

  const allPins = React.useMemo(() => computeMapPins(mapPoints, addresses), [mapPoints, addresses])

  const bounds = React.useMemo(() => {
    if (!allPins.length) return null
    let minLat = 90; let maxLat = -90; let minLng = 180; let maxLng = -180
    allPins.forEach((p) => {
      minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat)
      minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng)
    })
    return { minLat, maxLat, minLng, maxLng }
  }, [allPins])

  const center = bounds
    ? { lat: (bounds.minLat + bounds.maxLat) / 2, lng: (bounds.minLng + bounds.maxLng) / 2 }
    : { lat: 20, lng: 0 }

  // Google's own `zoomControl: true` (still passed below alongside
  // `disableDefaultUI: true`) is documented to survive that blanket
  // disable, but in this app it renders the +/− control invisible rather
  // than missing — its default white pill and dark glyph both get swept up
  // by the app's global dark-mode CSS (button/svg colour resets meant for
  // our own UI), so the control is there and clickable but not visible
  // against the map. Rather than fight that with ever more specific
  // overrides against a Google-owned DOM subtree we don't control, this
  // keeps a ref to the map instance and draws the exact same themed
  // MapNavBtn cluster WorldMap uses below — same look, same corner,
  // guaranteed visible in both themes.
  const mapRef = React.useRef<any>(null)
  const onLoad = React.useCallback((map: any) => {
    mapRef.current = map
    if (!bounds || allPins.length < 2) return
    const b = new window.google.maps.LatLngBounds(
      { lat: bounds.minLat, lng: bounds.minLng },
      { lat: bounds.maxLat, lng: bounds.maxLng },
    )
    map.fitBounds(b, 40)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bounds])

  const zoomBy = React.useCallback((delta: number) => {
    const map = mapRef.current
    if (!map) return
    const current = typeof map.getZoom === 'function' ? map.getZoom() : undefined
    map.setZoom(Math.min(21, Math.max(1, (current ?? 2) + delta)))
  }, [])

  // `useJsApiLoader` with the app-wide shared id (same one
  // `IPGeolocationCommon`/`RedditResultsViewer` already use), not a
  // per-instance `<LoadScript>`. Any template's small card AND its
  // full-screen dialog can both have a `LiveMap` mounted at once — opening
  // full screen while already in live mode meant two independent
  // `<LoadScript>`s racing to inject the same Google Maps script tag, and
  // the second one only ever resolved cleanly on a *second* attempt once
  // the first had finished. `useJsApiLoader` shares one loader (and its
  // promise) across every caller using this id, so simultaneous mounts just
  // await the same load instead of colliding.
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  })

  if (!allPins.length) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
        <Typography sx={{ fontSize: 12.5, color: tk.text3 }}>No geolocation provided</Typography>
      </Box>
    )
  }

  if (!isLoaded) {
    return (
      <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
        <Typography sx={{ fontSize: 12.5, color: tk.text3 }}>Loading map…</Typography>
      </Box>
    )
  }

  return (
    // `data-no-blur` opts this subtree out of ResultView's sample-preview
    // photo blur (blurPhotos prop, see ResultView.tsx) — Google Maps renders
    // its tiles as plain <img> elements, so the blanket "blur every <img>"
    // rule used for mockup avatars/photos was blurring the live map itself
    // into an unreadable smear. The map isn't a photo of the searched
    // person, so it should stay sharp even in the blurred sample preview.
    <Box data-no-blur sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        zoom={allPins.length === 1 ? 8 : 2}
        onLoad={onLoad}
        options={{ clickableIcons: false, disableDefaultUI: true, zoomControl: false }}
      >
        {allPins.map((p) => (
          <MarkerF
            key={p.key}
            position={{ lat: p.lat, lng: p.lng }}
            title={`${p.label}${p.precision === 'country' ? ' (country only)' : ''}`}
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE ?? 0,
              scale: p.precision === 'country' ? 15 : 9,
              fillColor: p.precision === 'country' ? brand.warning : brand.danger,
              fillOpacity: p.precision === 'country' ? 0.3 : 1,
              strokeColor: p.precision === 'country' ? brand.warning : '#fff',
              strokeWeight: p.precision === 'country' ? 2 : 2.5,
            }}
          />
        ))}
      </GoogleMap>

      {/*
        Google's own map panes (tiles, markers, its — now hidden — native
        controls) are absolutely-positioned layers stacked with their own
        z-index inside the map container, some of it quite high (the click
        target / controls panes in particular). Without an explicit zIndex
        of our own, these two overlays — plain later DOM siblings with no
        stacking context — were painting *underneath* those panes instead
        of on top, which is why both the zoom buttons and the legend were
        invisible even though they were rendering (confirmed empty in every
        screenshot so far, not just the zoom cluster added just now).
      */}
      <Box sx={{ position: 'absolute', right: 6, bottom: 6, zIndex: 10, display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <MapNavBtn onClick={() => zoomBy(1)}>+</MapNavBtn>
        <MapNavBtn onClick={() => zoomBy(-1)}>−</MapNavBtn>
      </Box>

      <Box sx={{
        position: 'absolute', left: 8, bottom: 8, zIndex: 10, maxWidth: 260,
        background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: '10px',
        padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: '5px',
      }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: `${brand.warning}55`, border: `2px dashed ${brand.warning}`, flex: 'none' }} />
          <Typography sx={{ fontSize: 9.5, color: tk.text2 }}>Country only, no exact point</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: brand.danger, border: '2px solid #fff', flex: 'none' }} />
          <Typography sx={{ fontSize: 9.5, color: tk.text2 }}>Exact coordinates</Typography>
        </Box>
      </Box>
    </Box>
  )
}

/** Shared look for the small text buttons in the Map card's header/toolbar. */
function mapBtnSx(tk: ReturnType<typeof useTokens>) {
  return {
    fontSize: 10, fontWeight: 700, cursor: 'pointer', borderRadius: '7px', padding: '4px 9px',
    border: `1px solid ${tk.border}`, background: tk.surface2, color: tk.text2, fontFamily: 'inherit', whiteSpace: 'nowrap' as const,
    '&:hover': { borderColor: tk.borderStrong, color: tk.text },
  }
}

// `addressCount` stays in the prop type (every call site already passes it)
// even though the live-only map no longer needs it for a pin-count caption —
// changing the signature would mean touching five template files for no
// behavioural gain.
export function MapCard({ mapPoints, addresses }: { addressCount: number; mapPoints: any[]; addresses: ValueRow[] }) {
  const tk = useTokens()
  const [fullscreen, setFullscreen] = React.useState(false)

  // Both call sites (the small ResCard, via its `fill`-driven flex content
  // box, and the full-screen dialog's own flex:1 body) already give this
  // wrapper a definite height, so `LiveMap` filling `100%` of it works the
  // same way in both places — no separate "small vs full-screen" sizing
  // branch needed. LiveMap used to default to a fixed 260px instead of
  // `100%` outside full screen, which is exactly why the zoom buttons
  // (anchored to LiveMap's own bottom-right corner) rendered off the edge
  // of the actually-visible, shorter small-card area and got clipped by
  // this Box's `overflow: hidden`.
  const renderMapBody = () => (
    <Box sx={{ height: '100%', width: '100%', minWidth: 0, overflow: 'hidden', borderRadius: `${radii.md}px` }}>
      <LiveMap mapPoints={mapPoints} addresses={addresses} />
    </Box>
  )

  return (
    <>
      <ResCard
        title="Map"
        fill
        action={
          <Box component="button" type="button" onClick={() => setFullscreen(true)} sx={mapBtnSx(tk)}>
            Full screen
          </Box>
        }
      >
        {renderMapBody()}
      </ResCard>

      {/*
        A real MUI Dialog (fullScreen), not a hand-rolled fixed-position
        overlay — Escape-to-close, focus trap, aria-hidden on the rest of the
        page, and the body scroll lock all come from MUI's own Modal
        machinery (the same thing `usePhotoLightbox`'s Dialog relies on)
        instead of a manually toggled `document.body.style.overflow` +
        a raw `document`-level keydown listener, which is what left the page
        stuck after closing via Esc.
      */}
      <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)} PaperProps={{ sx: { background: tk.bg } }}>
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
            padding: '14px 20px', borderBottom: `1px solid ${tk.border}`, flex: 'none', background: tk.surface,
          }}
          >
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 800, color: tk.text }}>Map</Typography>
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Box component="button" type="button" onClick={() => setFullscreen(false)} sx={mapBtnSx(tk)}>
                ✕ Exit full screen
              </Box>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, padding: '16px 20px' }}>
            {renderMapBody()}
          </Box>
        </Box>
      </Dialog>
    </>
  )
}
